// FieldIQ — Offline caches: lookup/bundle/walks IndexedDB + write queue sync

import { FIELDIQ_API_URL, authFetch } from "./api";

const _LOOKUP_DB_NAME = "fieldiq-lookup";
const _LOOKUP_STORE = "lookups";
const _LOOKUP_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000; // 30 days
// Bump when the lookup response shape changes to bust stale client caches
const _LOOKUP_CACHE_VERSION = 2;

function _openLookupDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(_LOOKUP_DB_NAME, 1);
    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(_LOOKUP_STORE)) {
        db.createObjectStore(_LOOKUP_STORE, { keyPath: "key" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function lookupCacheGet(key) {
  try {
    const db = await _openLookupDB();
    return new Promise((resolve) => {
      const tx = db.transaction(_LOOKUP_STORE, "readonly");
      const req = tx.objectStore(_LOOKUP_STORE).get(key);
      req.onsuccess = () => {
        const rec = req.result;
        if (!rec) return resolve(null);
        if (rec.version !== _LOOKUP_CACHE_VERSION) return resolve(null);
        if (Date.now() - rec.cachedAt > _LOOKUP_MAX_AGE_MS)
          return resolve(null);
        resolve(rec.payload);
      };
      req.onerror = () => resolve(null);
    });
  } catch {
    return null;
  }
}

export async function lookupCachePut(key, payload) {
  try {
    const db = await _openLookupDB();
    return new Promise((resolve) => {
      const tx = db.transaction(_LOOKUP_STORE, "readwrite");
      tx.objectStore(_LOOKUP_STORE).put({
        key,
        payload,
        cachedAt: Date.now(),
        version: _LOOKUP_CACHE_VERSION,
      });
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
    });
  } catch {
    /* non-fatal */
  }
}

export async function lookupCacheList() {
  try {
    const db = await _openLookupDB();
    return new Promise((resolve) => {
      const tx = db.transaction(_LOOKUP_STORE, "readonly");
      const req = tx.objectStore(_LOOKUP_STORE).getAll();
      req.onsuccess = () =>
        resolve(
          (req.result || [])
            .filter((r) => Date.now() - r.cachedAt <= _LOOKUP_MAX_AGE_MS)
            .sort((a, b) => b.cachedAt - a.cachedAt),
        );
      req.onerror = () => resolve([]);
    });
  } catch {
    return [];
  }
}

export async function lookupCacheClear() {
  try {
    const db = await _openLookupDB();
    return new Promise((resolve) => {
      const tx = db.transaction(_LOOKUP_STORE, "readwrite");
      tx.objectStore(_LOOKUP_STORE).clear();
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
    });
  } catch {
    // ignore
  }
}

// ═══════════════════════════════════════════════════════════════════
// OFFLINE BUNDLE — fetch, store, and serve walk bundles from IndexedDB
// ═══════════════════════════════════════════════════════════════════

const _BUNDLE_DB_NAME = "fieldiq-bundle";
const _BUNDLE_STORE = "bundles";
const _BUNDLE_SCHEMA_VERSION = 1;

function _openBundleDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(_BUNDLE_DB_NAME, 1);
    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(_BUNDLE_STORE)) {
        db.createObjectStore(_BUNDLE_STORE, { keyPath: "pcId" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function _decompressGzip(arrayBuffer) {
  // DecompressionStream is supported in Chrome 80+ and Safari 16.4+
  if (typeof DecompressionStream !== "undefined") {
    const ds = new DecompressionStream("gzip");
    const writer = ds.writable.getWriter();
    writer.write(arrayBuffer);
    writer.close();
    const chunks = [];
    const reader = ds.readable.getReader();
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      chunks.push(value);
    }
    const total = chunks.reduce((n, c) => n + c.length, 0);
    const out = new Uint8Array(total);
    let offset = 0;
    for (const c of chunks) {
      out.set(c, offset);
      offset += c.length;
    }
    return new TextDecoder().decode(out);
  }
  // Fallback: assume plain JSON (non-compressed response)
  return new TextDecoder().decode(arrayBuffer);
}

async function fetchAndCacheBundle(pcId, token) {
  if (!pcId) return null;
  try {
    const metaResp = await fetch(
      `${FIELDIQ_API_URL}/api/v1/lookup/bundle/url`,
      { headers: token ? { Authorization: `Bearer ${token}` } : {} },
    );
    if (!metaResp.ok) return null; // no bundle yet — silent
    const { url, addr_count } = await metaResp.json();
    if (!url) return null;

    const bundleResp = await fetch(url);
    if (!bundleResp.ok) return null;

    const raw = await bundleResp.arrayBuffer();
    const json = await _decompressGzip(raw);
    const bundle = JSON.parse(json);

    if (bundle.schema_version !== _BUNDLE_SCHEMA_VERSION) {
      console.warn(
        `[FieldIQ] Bundle schema v${bundle.schema_version} ≠ expected v${_BUNDLE_SCHEMA_VERSION} — ignoring`,
      );
      return null;
    }

    // Build O(1) address index
    const index = {};
    for (const entry of bundle.addresses || []) {
      if (entry.address) index[entry.address.trim().toLowerCase()] = entry;
    }
    bundle._index = index;
    bundle._cachedAt = Date.now();

    // Persist in IndexedDB
    const db = await _openBundleDB();
    await new Promise((resolve) => {
      const tx = db.transaction(_BUNDLE_STORE, "readwrite");
      tx.objectStore(_BUNDLE_STORE).put({ pcId, bundle });
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
    });

    return bundle;
  } catch (err) {
    console.warn("[FieldIQ] Bundle fetch failed:", err);
    return null;
  }
}

async function loadCachedBundle(pcId) {
  if (!pcId) return null;
  try {
    const db = await _openBundleDB();
    return new Promise((resolve) => {
      const tx = db.transaction(_BUNDLE_STORE, "readonly");
      const req = tx.objectStore(_BUNDLE_STORE).get(pcId);
      req.onsuccess = () => {
        const rec = req.result;
        resolve(rec?.bundle || null);
      };
      req.onerror = () => resolve(null);
    });
  } catch {
    return null;
  }
}

function bundleAddressLookup(bundle, address) {
  if (!bundle?._index) return null;
  return bundle._index[address.trim().toLowerCase()] || null;
}

// ═══════════════════════════════════════════════════════════════════
// OFFLINE ACTIVITY CACHE — walk cache + pending write queue
// ═══════════════════════════════════════════════════════════════════

const _OFFLINE_DB_NAME = "fieldiq-offline";
const _OFFLINE_DB_VERSION = 1;

function _openOfflineDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(_OFFLINE_DB_NAME, _OFFLINE_DB_VERSION);
    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains("walks")) {
        db.createObjectStore("walks", { keyPath: "walkId" });
      }
      if (!db.objectStoreNames.contains("pending_sync")) {
        const ps = db.createObjectStore("pending_sync", {
          keyPath: "id",
          autoIncrement: true,
        });
        ps.createIndex("by_created", "created_at");
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function saveWalksToCache(walks) {
  try {
    const db = await _openOfflineDB();
    const existing = await new Promise((resolve) => {
      const tx = db.transaction("walks", "readonly");
      const req = tx.objectStore("walks").getAll();
      req.onsuccess = () =>
        resolve(
          Object.fromEntries((req.result || []).map((r) => [r.walkId, r])),
        );
      req.onerror = () => resolve({});
    });
    const tx2 = db.transaction("walks", "readwrite");
    const store = tx2.objectStore("walks");
    for (const w of walks) {
      if (!w._apiId) continue;
      store.put({
        walkId: w._apiId,
        data: w,
        stops: existing[w._apiId]?.stops ?? null,
        synced_at: Date.now(),
      });
    }
  } catch {}
}

export async function loadWalksFromCache() {
  try {
    const db = await _openOfflineDB();
    return new Promise((resolve) => {
      const tx = db.transaction("walks", "readonly");
      const req = tx.objectStore("walks").getAll();
      req.onsuccess = () =>
        resolve((req.result || []).map((r) => r.data).filter(Boolean));
      req.onerror = () => resolve([]);
    });
  } catch {
    return [];
  }
}

export async function saveStopsToCache(walkId, stops) {
  if (!walkId) return;
  try {
    const db = await _openOfflineDB();
    const existing = await new Promise((resolve) => {
      const tx = db.transaction("walks", "readonly");
      const req = tx.objectStore("walks").get(walkId);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
    });
    const rec = existing || { walkId, data: null, synced_at: Date.now() };
    rec.stops = stops;
    const tx2 = db.transaction("walks", "readwrite");
    tx2.objectStore("walks").put(rec);
  } catch {}
}

export async function loadStopsFromCache(walkId) {
  if (!walkId) return null;
  try {
    const db = await _openOfflineDB();
    return new Promise((resolve) => {
      const tx = db.transaction("walks", "readonly");
      const req = tx.objectStore("walks").get(walkId);
      req.onsuccess = () => resolve(req.result?.stops ?? null);
      req.onerror = () => resolve(null);
    });
  } catch {
    return null;
  }
}

export async function queueOfflineWrite(endpoint, method, body) {
  try {
    const db = await _openOfflineDB();
    const tx = db.transaction("pending_sync", "readwrite");
    tx.objectStore("pending_sync").add({
      endpoint,
      method,
      body,
      attempts: 0,
      created_at: Date.now(),
    });
  } catch {}
}

async function _getPendingWrites() {
  try {
    const db = await _openOfflineDB();
    return new Promise((resolve) => {
      const tx = db.transaction("pending_sync", "readonly");
      const req = tx.objectStore("pending_sync").index("by_created").getAll();
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => resolve([]);
    });
  } catch {
    return [];
  }
}

async function _deletePendingWrite(id) {
  try {
    const db = await _openOfflineDB();
    const tx = db.transaction("pending_sync", "readwrite");
    tx.objectStore("pending_sync").delete(id);
  } catch {}
}

async function _incrementPendingAttempts(id) {
  try {
    const db = await _openOfflineDB();
    const existing = await new Promise((resolve) => {
      const tx = db.transaction("pending_sync", "readonly");
      const req = tx.objectStore("pending_sync").get(id);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
    });
    if (!existing) return;
    const tx2 = db.transaction("pending_sync", "readwrite");
    tx2.objectStore("pending_sync").put({
      ...existing,
      attempts: (existing.attempts || 0) + 1,
      last_attempt_at: Date.now(),
    });
  } catch {}
}

const _MAX_SYNC_ATTEMPTS = 5;
let _syncInProgress = false;

export async function flushSyncQueue(onComplete) {
  if (_syncInProgress || !navigator.onLine) return;
  _syncInProgress = true;
  try {
    const pending = await _getPendingWrites();
    if (!pending.length) return;
    let synced = 0;
    let failed = 0;
    for (const entry of pending) {
      if ((entry.attempts || 0) >= _MAX_SYNC_ATTEMPTS) {
        failed++;
        continue;
      }
      try {
        const result = await authFetch(entry.endpoint, {
          method: entry.method,
          body: entry.body,
        });
        if (!result.error) {
          await _deletePendingWrite(entry.id);
          synced++;
        } else {
          await _incrementPendingAttempts(entry.id);
          failed++;
        }
      } catch {
        await _incrementPendingAttempts(entry.id);
      }
    }
    if (onComplete) onComplete(synced, failed);
  } finally {
    _syncInProgress = false;
  }
}

// ═══════════════════════════════════════════════════════════════════
// GEO LOOKUP — UI components
